import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const escape = value => String(value ?? "").replace(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char])
const icon = name => `<span class="material-symbols-rounded" aria-hidden="true">${name}</span>`
const date = value => new Date(`${value}T12:00:00Z`)
const addDays = (value, days) => { const d = date(value); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10) }
const monday = value => addDays(value, -((date(value).getUTCDay() + 6) % 7))
const format = (value, options = { month: "short", day: "numeric" }) => date(value).toLocaleDateString("en-CA", { ...options, timeZone: "UTC" })
const duration = minutes => minutes >= 60 && minutes % 60 === 0 ? `${minutes / 60} ${minutes === 60 ? "hr" : "hrs"}` : `${minutes} min`

export default class extends Controller {
  static values = { base: String }
  static targets = ["notice", "weekName", "weekRange", "thisWeek", "summary", "people", "dayTabs", "board", "dialog", "kicker", "drawerNotice", "form", "ownerOptions", "minuteOptions", "comments", "reply", "outcome", "outcomeActions", "deleteConfirm", "footer", "saveStatus", "toast", "toastMessage", "undo"]

  connect() {
    this.active = true
    this.pending = {}
    this.person = "all"
    this.observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width
      const changed = this.mobile !== (width < 720) || this.autoCollapsed !== (width < 1250)
      this.mobile = width < 720
      this.autoCollapsed = width < 1250
      this.element.classList.toggle("is-mobile", this.mobile)
      if (changed && this.boardData) this.renderBoard()
    })
    this.observer.observe(this.element)
    this.load().catch(error => this.error(error))
    this.poller = setInterval(() => this.poll(), 20000)
  }

  disconnect() {
    this.active = false
    clearInterval(this.poller)
    clearTimeout(this.saveTimer)
    clearTimeout(this.toastTimer)
    this.observer.disconnect()
  }

  async api(path = "", method = "GET", body) {
    const response = await fetch(`${this.baseValue}${path}`, {
      method, credentials: "same-origin", cache: "no-store",
      headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: body === undefined ? undefined : JSON.stringify(body)
    })
    if (response.redirected || !response.headers.get("content-type")?.includes("application/json")) throw new Error("Your session may have expired. Reload and sign in again; keep a copy of any unsaved text.")
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || "This change could not be saved. Please try again.")
    return result
  }

  error(error) {
    if (!this.active) return
    const target = this.dialogTarget.open ? this.drawerNoticeTarget : this.noticeTarget
    target.textContent = error.message || "The meeting board could not be loaded. Please reload and try again."
    target.hidden = false
  }

  async load(week = this.boardData?.week) {
    const request = this.loadRequest = (this.loadRequest || 0) + 1
    const result = await this.api(`/data${week ? `?week=${encodeURIComponent(week)}` : ""}`)
    if (!this.active || request !== this.loadRequest) return
    const resetDay = this.boardData?.week !== result.week
    this.boardData = result
    if (resetDay) this.selectedDay = this.days.includes(result.today) ? result.today : result.week
    this.noticeTarget.hidden = true
    this.renderBoard()
  }

  async poll() {
    if (!this.boardData || document.hidden || this.busy || this.saving || this.dirty || this.dragId || this.loadingPoint) return
    try {
      await this.load()
      const id = this.point?.id
      if (id && this.dialogTarget.open) {
        const point = await this.api(`/${id}`)
        if (this.point?.id === id && !this.dirty && !this.saving && !this.busy) {
          this.point.comments = point.comments
          this.renderComments()
        }
      }
    } catch (error) { this.error(error) }
  }

  get days() { return Array.from({ length: 5 }, (_, i) => addDays(this.boardData.week, i)) }
  get dirty() { return Object.keys(this.pending).length > 0 }
  personFor(id) { return this.boardData.people.find(person => person.id === Number(id)) || { name: "Team", initial: "T", color: "#0C7BB3" } }
  avatar(person) { return `<span class="mt-avatar" aria-hidden="true" style="--person-color:${escape(person.color)}">${escape(person.initial)}</span>` }
  pointsFor(day) { return this.boardData.points.filter(point => (point.meeting_date || "") === day).sort((a, b) => a.position - b.position || a.id - b.id) }
  nextDay(source) {
    let target = source < this.boardData.today ? this.boardData.today : addDays(source, 1)
    while ([0, 6].includes(date(target).getUTCDay())) target = addDays(target, 1)
    return target
  }

  renderBoard() {
    const week = this.boardData.week, thisWeek = monday(this.boardData.today)
    this.weekNameTarget.textContent = week === thisWeek ? "This week" : week === addDays(thisWeek, 7) ? "Next week" : week === addDays(thisWeek, -7) ? "Last week" : `Week of ${format(week)}`
    this.weekRangeTarget.textContent = `${format(week)} – ${format(addDays(week, 4), { month: "short", day: "numeric", year: "numeric" })}`
    this.thisWeekTarget.hidden = week === thisWeek
    const dated = this.boardData.points.filter(point => point.meeting_date)
    this.summaryTarget.textContent = `${dated.length} points · ${dated.filter(point => point.discussed).length} discussed`
    this.peopleTarget.innerHTML = `<button type="button" aria-pressed="${this.person === "all"}" data-person="all" data-action="admin-meetings#filter">Everyone</button>` + this.boardData.people.map(person => `<button type="button" aria-pressed="${this.person === String(person.id)}" data-person="${person.id}" data-action="admin-meetings#filter">${this.avatar(person)}${escape(person.name)}</button>`).join("")
    this.dayTabsTarget.innerHTML = ["", ...this.days].map(day => `<button type="button" data-day="${day}" data-action="admin-meetings#selectDay" aria-pressed="${this.selectedDay === day}" class="${day === this.boardData.today ? "is-today" : ""}">${day ? format(day, { weekday: "short" }) : "Inbox"}<small>${this.pointsFor(day).filter(point => !point.discussed).length}</small></button>`).join("")
    this.boardTarget.innerHTML = ["", ...this.days].map(day => this.column(day)).join("")
  }

  column(day) {
    const points = this.pointsFor(day), open = points.filter(point => !point.discussed), done = points.filter(point => point.discussed)
    const collapsed = !day && !this.mobile && (this.inboxCollapsed ?? this.autoCollapsed)
    const classes = `mt-column ${!day ? "mt-column--inbox" : ""} ${day === this.boardData.today ? "is-today" : ""} ${this.selectedDay === day ? "is-selected" : ""} ${collapsed ? "is-collapsed" : ""}`
    if (collapsed) return `<section class="${classes}" data-day=""><button type="button" class="mt-inbox-strip" aria-label="Expand Unscheduled" data-action="admin-meetings#toggleInbox">${icon("inbox")}<span class="mt-count">${open.length}</span><strong>Unscheduled</strong>${icon("chevron_right")}</button></section>`
    const minutes = points.reduce((sum, point) => sum + point.minutes, 0), length = this.boardData.days[day] || 60
    const title = day ? `${format(day, { weekday: "short" })}` : "Unscheduled"
    const timebar = day ? `<div class="mt-time ${minutes > length ? "is-over" : ""}"><span class="mt-time-bar"><i style="width:${Math.min(100, minutes / length * 100)}%"></i></span><span>${minutes} min of</span><select aria-label="Meeting length for ${format(day)}" data-day="${day}" data-action="admin-meetings#changeLength">${this.boardData.lengths.map(value => `<option value="${value}" ${value === length ? "selected" : ""}>${value < 120 ? `${value} min` : duration(value)}</option>`).join("")}</select></div>` : '<p class="mt-column-description">Not on a day yet. Drag onto a day to schedule.</p>'
    const leftover = day && day < this.boardData.today && open.length ? `<div class="mt-leftovers"><span>${icon("history")}${open.length} ${open.length === 1 ? "point" : "points"} not discussed</span><button class="mt-button" type="button" data-day="${day}" data-action="admin-meetings#carryDay">Move to ${format(this.nextDay(day), { weekday: "short" })}</button></div>` : ""
    return `<section class="${classes}" data-day="${day}" aria-label="${day ? format(day, { weekday: "long", month: "short", day: "numeric" }) : "Unscheduled"}"><header class="mt-column-head"><div class="mt-column-title"><h2>${title}</h2>${day ? `<span class="mt-date">${format(day)}</span>` : ""}${day === this.boardData.today ? '<span class="mt-today">Today</span>' : ""}<span class="mt-count">${open.length}</span>${day ? `<button type="button" class="mt-icon" aria-label="Copy agenda for ${format(day)}" data-day="${day}" data-action="admin-meetings#copyAgenda">${icon("content_copy")}</button>` : this.mobile ? "" : `<button type="button" class="mt-icon" aria-label="Collapse Unscheduled" data-action="admin-meetings#toggleInbox">${icon("chevron_left")}</button>`}</div>${timebar}</header>${leftover}<div class="mt-card-list">${open.length ? open.map((point, i) => this.card(point, i + 1)).join("") : `<div class="mt-empty">${!day ? "Everything is scheduled" : day < this.boardData.today ? "Nothing left to discuss" : "Drag points here"}</div>`}</div>${done.length ? `<div class="mt-discussed"><div class="mt-discussed-label">Discussed · ${done.length}</div>${done.map(point => this.card(point)).join("")}</div>` : ""}<button type="button" class="mt-add-point" data-day="${day}" data-action="admin-meetings#newPoint" aria-label="Add a point to ${day ? format(day) : "Unscheduled"}">${icon("add")}Add a point</button></section>`
  }

  card(point, number) {
    const person = this.personFor(point.owner_id)
    const classes = `mt-point ${point.discussed ? "mt-point--discussed" : ""} ${this.person !== "all" && this.person !== String(point.owner_id) ? "is-dimmed" : ""} ${this.point?.id === point.id && this.dialogTarget.open ? "is-open" : ""}`
    const attributes = `class="${classes}" data-id="${point.id}" role="button" tabindex="0" draggable="${!point.discussed && !this.mobile}" data-action="click->admin-meetings#openPoint keydown.enter->admin-meetings#openPoint keydown.space->admin-meetings#openPoint"`
    if (point.discussed) return `<article ${attributes}><span class="mt-check">${icon("check")}</span><div class="mt-discussed-copy"><strong>${escape(point.title)}</strong>${point.outcome ? `<div class="mt-point-outcome">${escape(point.outcome)}</div>` : ""}</div></article>`
    return `<article ${attributes}><div class="mt-point-title"><span class="mt-number">${number}</span><strong>${escape(point.title)}</strong>${point.priority ? '<span class="material-symbols-rounded mt-flag" title="Discuss first" aria-label="Discuss first">flag</span>' : ""}</div><div class="mt-point-meta"><span class="mt-owner">${this.avatar(person)}${escape(person.name)}</span><span class="mt-topic mt-topic--${escape(point.topic.toLowerCase().replaceAll(" ", "-"))}">${escape(point.topic)}</span><span class="mt-point-detail">${point.comments_count ? `<span>${icon("chat_bubble")}${point.comments_count}</span>` : ""}<span>${point.minutes} min</span></span></div></article>`
  }

  filter(event) { this.person = event.currentTarget.dataset.person; this.renderBoard() }
  selectDay(event) { this.selectedDay = event.currentTarget.dataset.day; this.renderBoard() }
  toggleInbox() { this.inboxCollapsed = !(this.inboxCollapsed ?? this.autoCollapsed); this.renderBoard() }
  async changeWeek(event) { if (this.boardData) await this.run(() => this.load(addDays(this.boardData.week, Number(event.currentTarget.dataset.offset)))) }
  async currentWeek() { if (this.boardData) await this.run(() => this.load(monday(this.boardData.today))) }
  async changeLength(event) {
    const { day } = event.currentTarget.dataset, length = Number(event.currentTarget.value)
    await this.run(async () => { await this.api("/length", "PUT", { meeting_date: day, length_minutes: length }); await this.load() })
  }
  async newPoint(event) {
    const day = event?.currentTarget.dataset.day || null
    if (!this.boardData || this.busy || this.loadingPoint) return
    if (this.dialogTarget.open && !(await this.close())) return
    this.showDrawer({ title: "", notes: "", owner_id: this.boardData.current_user_id, minutes: 15, topic: "General", meeting_date: day, priority: false, discussed: false, outcome: "", comments: [], client_key: crypto.randomUUID() })
  }
  async openPoint(event) {
    event.preventDefault()
    if (this.busy || this.loadingPoint) return
    this.loadingPoint = true
    try {
      const point = await this.api(`/${event.currentTarget.dataset.id}`)
      if (this.active) this.showDrawer(point)
    } catch (error) { this.error(error) }
    finally { this.loadingPoint = false }
  }
  showDrawer(point) {
    this.point = point
    this.pending = {}
    this.replyTarget.value = ""
    this.replyKeyValue = null
    this.drawerNoticeTarget.hidden = true
    this.saveStatusTarget.textContent = "Changes save automatically"
    this.cancelDelete()
    for (const name of ["title", "notes", "topic"]) if (name !== "topic") this.formTarget.elements[name].value = point[name] || ""
    this.outcomeTarget.value = point.outcome || ""
    this.formTarget.elements.priority.checked = point.priority
    this.formTarget.elements.topic.innerHTML = this.boardData.topics.map(topic => `<option ${topic === point.topic ? "selected" : ""}>${escape(topic)}</option>`).join("")
    this.renderChoices()
    this.renderPointState()
    this.renderComments()
    this.dialogTarget.append(this.toastTarget)
    this.dialogTarget.showModal()
    this.formTarget.elements.title.focus()
    this.renderBoard()
  }
  renderChoices() {
    this.ownerOptionsTarget.innerHTML = this.boardData.people.map(person => `<button type="button" data-field="owner_id" data-value="${person.id}" aria-pressed="${person.id === this.point.owner_id}" data-action="admin-meetings#choose">${this.avatar(person)}${escape(person.name)}</button>`).join("")
    this.minuteOptionsTarget.innerHTML = this.boardData.minutes.map(minutes => `<button type="button" data-field="minutes" data-value="${minutes}" aria-pressed="${minutes === this.point.minutes}" data-action="admin-meetings#choose">${duration(minutes)}</button>`).join("")
  }
  renderPointState() {
    const point = this.point
    const days = new Set(Array.from({ length: 21 }, (_, i) => addDays(this.boardData.week, i)).filter(day => ![0, 6].includes(date(day).getUTCDay())))
    if (point.meeting_date) days.add(point.meeting_date)
    this.formTarget.elements.meeting_date.innerHTML = '<option value="">Unscheduled</option>' + [...days].sort().map(day => `<option value="${day}">${format(day, { weekday: "short", month: "short", day: "numeric" })}${day === this.boardData.today ? " (today)" : ""}</option>`).join("")
    this.formTarget.elements.meeting_date.value = point.meeting_date || ""
    const index = this.pointsFor(point.meeting_date || "").filter(item => !item.discussed).findIndex(item => item.id === point.id)
    this.kickerTarget.textContent = `${point.meeting_date ? format(point.meeting_date, { weekday: "long", month: "short", day: "numeric" }) : "Unscheduled"}${!point.discussed && index >= 0 ? ` · #${index + 1}` : ""}`
    this.outcomeActionsTarget.innerHTML = point.discussed ? `<span class="mt-done-badge">${icon("check_circle")}Discussed</span><button type="button" class="mt-button" data-action="admin-meetings#mark" data-discussed="false">Reopen</button>` : `<button type="button" class="mt-button mt-discuss" data-action="admin-meetings#mark" data-discussed="true">${icon("check")}Mark discussed</button>${point.meeting_date ? `<button type="button" class="mt-button" data-action="admin-meetings#carryPoint">Carry over to ${format(this.nextDay(point.meeting_date), { weekday: "short" })}</button>` : ""}`
  }
  renderComments() {
    this.commentsTarget.innerHTML = this.point.comments?.length ? this.point.comments.map(comment => {
      const person = this.personFor(comment.author_id)
      const timestamp = new Date(comment.created_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Toronto" })
      return `<div class="mt-comment">${this.avatar(person)}<div class="mt-comment-content"><div class="mt-comment-heading">${escape(person.name)}<small>${escape(timestamp)} ET</small></div><div class="mt-comment-body">${escape(comment.body)}</div></div></div>`
    }).join("") : '<p class="mt-comment-empty">Questions and replies about this point go here, so they stay with it.</p>'
  }
  preventSubmit(event) { event.preventDefault() }
  fieldChanged(event) {
    const field = event.target.name
    if (!this.point || !["title", "notes", "topic", "priority", "outcome"].includes(field)) return
    this.edit(field, event.target.type === "checkbox" ? event.target.checked : event.target.value, event.target.tagName !== "TEXTAREA")
  }
  choose(event) { this.edit(event.currentTarget.dataset.field, Number(event.currentTarget.dataset.value), true); this.renderChoices() }
  edit(field, value, immediate = false) {
    this.point[field] = value
    this.pending[field] = value
    this.saveStatusTarget.textContent = "Unsaved changes…"
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.flush(), immediate ? 0 : 500)
  }

  async flush() {
    clearTimeout(this.saveTimer)
    if (this.saving) { const ok = await this.saving; return ok && this.dirty ? this.flush() : ok }
    if (!this.point || !this.dirty) return true
    if (!this.point.title.trim()) {
      if (!this.point.id) return true // An empty local draft is never persisted.
      this.error(new Error("Please give this point a title before closing it."))
      this.saveStatusTarget.textContent = "Not saved"
      return false
    }
    this.saving = this.savePending()
    const result = await this.saving
    this.saving = null
    return result
  }
  async savePending() {
    while (this.dirty) {
      const patch = this.pending
      this.pending = {}
      const point = this.point
      this.saveStatusTarget.textContent = "Saving…"
      try {
        const saved = await this.api(point.id ? `/${point.id}` : "", point.id ? "PATCH" : "POST", { point: point.id ? patch : { ...point, ...patch } })
        this.point = { ...point, ...saved, ...this.pending }
        this.upsert(saved)
        this.drawerNoticeTarget.hidden = true
        this.saveStatusTarget.textContent = "All changes saved"
        this.renderPointState()
      } catch (error) {
        this.pending = { ...patch, ...this.pending }
        this.saveStatusTarget.textContent = "Not saved — try again"
        this.error(error)
        return false
      }
    }
    return true
  }
  upsert(point) {
    const index = this.boardData.points.findIndex(item => item.id === point.id)
    if (point.meeting_date && !this.days.includes(point.meeting_date)) {
      if (index !== -1) this.boardData.points.splice(index, 1)
      this.renderBoard()
      return
    }
    if (index === -1) this.boardData.points.push({ comments_count: 0, ...point })
    else this.boardData.points[index] = { ...this.boardData.points[index], ...point }
    this.renderBoard()
  }
  async close(event) {
    event?.preventDefault()
    if (this.busy || !(await this.flush())) return false
    this.finishClose()
    return true
  }
  finishClose() {
    clearTimeout(this.saveTimer)
    this.pending = {}
    this.point = null
    this.dialogTarget.close()
    this.element.append(this.toastTarget)
    this.renderBoard()
  }
  backdrop(event) { if (event.target === this.dialogTarget && event.clientX < this.dialogTarget.getBoundingClientRect().left) this.close(event) }
  beforeCache() {
    if (this.dialogTarget.open) this.dialogTarget.close()
    this.element.append(this.toastTarget)
    this.toastTarget.hidden = true
  }
  beforeUnload(event) { if (this.saving || (this.dirty && (this.point?.id || this.point?.title.trim()))) { event.preventDefault(); event.returnValue = "" } }
  async beforeVisit(event) {
    if (!this.saving && !this.dirty) return
    event.preventDefault()
    if (await this.flush()) { this.pending = {}; Turbo.visit(event.detail.url) }
  }

  async run(action) {
    if (this.busy) return false
    this.busy = true
    try { await action(); return true }
    catch (error) { this.error(error); return false }
    finally { this.busy = false }
  }
  async persisted() {
    if (!(await this.flush())) return false
    if (!this.point?.id) { this.error(new Error("Add a title first.")); this.formTarget.elements.title.focus(); return false }
    return true
  }
  async changeDay(event) {
    const target = event.currentTarget.value || null
    if (!this.point.id && !this.point.title.trim()) { this.point.meeting_date = target; this.renderPointState(); return }
    await this.run(async () => {
      if (!(await this.persisted())) return
      const saved = await this.api(`/${this.point.id}/move`, "PATCH", { meeting_date: target })
      this.point = { ...this.point, ...saved }
      await this.load()
      this.renderPointState()
      this.toast("Point moved", saved.undo_token)
    })
    this.renderPointState()
  }
  async mark(event) {
    const discussed = event.currentTarget.dataset.discussed === "true"
    await this.run(async () => {
      if (!(await this.persisted())) return
      const saved = await this.api(`/${this.point.id}/mark`, "PATCH", { discussed })
      this.point = { ...this.point, ...saved }
      await this.load()
      if (discussed) this.finishClose(); else this.renderPointState()
      this.toast(discussed ? "Point marked discussed" : "Point reopened", saved.undo_token)
    })
  }
  async carryPoint() {
    const target = this.nextDay(this.point.meeting_date)
    await this.run(async () => {
      if (!(await this.persisted())) return
      const saved = await this.api(`/${this.point.id}/move`, "PATCH", { meeting_date: target })
      this.point = { ...this.point, ...saved }
      await this.load()
      this.renderPointState()
      this.toast(`Moved to ${format(target, { weekday: "short", month: "short", day: "numeric" })}`, saved.undo_token)
    })
  }
  async carryDay(event) {
    const day = event.currentTarget.dataset.day
    await this.run(async () => {
      const saved = await this.api("/carry", "POST", { meeting_date: day })
      await this.load()
      this.toast(`${saved.count} points moved to ${format(saved.meeting_date)}`, saved.undo_token)
    })
  }
  replyKey(event) { if (event.key === "Enter" && !event.shiftKey && !event.isComposing) { event.preventDefault(); this.postComment() } }
  async postComment(event) {
    event?.preventDefault()
    const body = this.replyTarget.value.trim()
    if (!body) return
    await this.run(async () => {
      if (!(await this.persisted())) return
      const key = this.replyKeyValue ||= crypto.randomUUID()
      const saved = await this.api(`/${this.point.id}/comment`, "POST", { body, client_key: key })
      this.point.comments = saved.comments
      // Keep a reply typed while the previous one was being posted.
      if (this.replyTarget.value.trim() === body) this.replyTarget.value = ""
      this.replyKeyValue = null
      this.renderComments()
      await this.load()
    })
  }
  askDelete() { this.deleteConfirmTarget.hidden = false; this.footerTarget.hidden = true }
  cancelDelete() { this.deleteConfirmTarget.hidden = true; this.footerTarget.hidden = false }
  async deletePoint() {
    await this.run(async () => {
      if (!this.point.id && !this.saving) { this.finishClose(); return }
      if (!(await this.persisted())) return
      const saved = await this.api(`/${this.point.id}`, "DELETE")
      await this.load()
      this.finishClose()
      this.toast("Point deleted", saved.undo_token)
    })
  }
  toast(message, token) {
    clearTimeout(this.toastTimer)
    this.toastMessageTarget.textContent = message
    this.undoToken = token
    this.undoTarget.hidden = !token
    this.toastTarget.hidden = false
    this.toastTimer = setTimeout(() => { this.toastTarget.hidden = true; this.undoToken = null }, 4000)
  }
  async undo() {
    const token = this.undoToken
    if (!token) return
    await this.run(async () => {
      if (!(await this.flush())) return
      await this.api("/undo", "POST", { token })
      if (this.point?.id) {
        const saved = await this.api(`/${this.point.id}`)
        this.point = { ...this.point, ...saved }
        this.renderPointState()
      }
      await this.load()
      this.toast("Change undone")
    })
  }
  async copyAgenda(event) {
    const day = event.currentTarget.dataset.day
    const text = [`${format(day, { weekday: "long", month: "short", day: "numeric" })} meeting`, ...this.pointsFor(day).filter(point => !point.discussed).map((point, i) => `${i + 1}. ${point.title} (${this.personFor(point.owner_id).name}, ${point.minutes} min)`)].join("\n")
    try { await navigator.clipboard.writeText(text); this.toast(`Agenda for ${format(day, { weekday: "short", month: "short", day: "numeric" })} copied`) }
    catch (_) { this.error(new Error("Clipboard access was denied. Allow clipboard access and try again.")) }
  }
  dragStart(event) {
    const card = event.target.closest('.mt-point[draggable="true"]')
    if (!card || this.busy) { event.preventDefault(); return }
    this.dragId = Number(card.dataset.id)
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", String(this.dragId))
    card.classList.add("is-dragging")
  }
  clearDrop() { this.boardTarget.querySelectorAll(".is-drop-target,.is-drop-before,.is-drop-end").forEach(node => node.classList.remove("is-drop-target", "is-drop-before", "is-drop-end")) }
  dragOver(event) {
    if (!this.dragId) return
    const column = event.target.closest(".mt-column")
    if (!column) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.clearDrop()
    column.classList.add("is-drop-target")
    const cards = [...column.querySelectorAll('.mt-point[draggable="true"]')].filter(card => Number(card.dataset.id) !== this.dragId)
    const before = cards.find(card => event.clientY < card.getBoundingClientRect().top + card.getBoundingClientRect().height / 2)
    this.dropTarget = { meeting_date: column.dataset.day || null, before_id: before ? Number(before.dataset.id) : null }
    if (before) before.classList.add("is-drop-before"); else column.querySelector(".mt-card-list")?.classList.add("is-drop-end")
  }
  dragEnd() { this.clearDrop(); this.boardTarget.querySelector(".is-dragging")?.classList.remove("is-dragging"); this.dragId = null; this.dropTarget = null }
  async drop(event) {
    if (!this.dragId || !this.dropTarget) return
    event.preventDefault()
    const id = this.dragId, target = this.dropTarget
    this.dragEnd()
    await this.run(async () => {
      const saved = await this.api(`/${id}/move`, "PATCH", target)
      await this.load()
      this.toast("Agenda updated", saved.undo_token)
    })
  }
}
